<template>
  <div>
    <div class="form-group row">
      <div class="col-md-8 text-left">
        <label>Company Name(s)</label>
        <el-input type="search"
               name="search"
               placeholder="Search"
               clearable
               @keyup.enter.native="searchSubmit"
               v-validate="'required'"
               v-model="query.search" />
        <small class="text-danger" v-show="errors.has('search')">
          {{ getError('search') }}
        </small>
      </div>
      <div class="col-md-3 text-left">
        <label>Company Type</label>
        <el-select class="select"
                   size="large"
                   placeholder="Company Type"
                   clearable
                   v-model="query.types">
          <el-option v-for="option in types"
                     class="select"
                     :value="option.value"
                     :label="option.label"
                     :key="option.label">
          </el-option>
        </el-select>
      </div>
      <div class="col-md-1 text-left" style="padding-left: 0;">
        <label>Active</label>
        <p-switch v-model="query.active" type="primary" on-text="YES" off-text="NO" style="display:block"></p-switch>
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <button type="button" :disabled="isSearchButtonDisabled" class="btn btn-fill btn-info pull-right" @click="searchSubmit">
          Search
        </button>
        <button type="button" :disabled="isClearButtonDisabled" class="btn btn-fill btn-info pull-right" @click="clear">
          Clear
        </button>
        <button type="button" @click="fetchAll" style="float: right" class="btn btn-fill btn-info">
          View All
        </button>
      </div>
    </div>
    <div>
      <div v-if="loading" style="text-align: center;">
        <img src="static/img/loading.gif"/>
      </div>
      <el-table class="wrap-whole-words table-striped indexed-table-column-width"
                v-else
                :data="tableData"
                border
                style="width: 100%"
                :max-height="tableHeight(500)">
        <el-table-column
          :width="indexColumnWidth"
          align="center"
          label="#"
          type="index">
        </el-table-column>
        <el-table-column
          min-width="200"
          :sortable="true"
          sort-by="_source.name"
          label="Name">
          <template slot-scope="props">
            <span class="invisible">{{props.row.id}}</span>
            <a href="#" @click.prevent="editCompany(props.row)">{{props.row.name}}</a>
          </template>
        </el-table-column>
        <el-table-column
          min-width="200"
          label="Types">
          <template slot-scope="props">
            {{props.row.types | toCsv}}
          </template>
        </el-table-column>
        <el-table-column
          width="110"
          fixed="right"
          label="Actions">
          <template slot-scope="props">
            <p-button type="success" size="sm" icon>
              <i class="fa fa-edit" @click="editCompany(props.row)"></i>
            </p-button>
            <p-button type="danger" v-if="canEdit" size="sm" icon @click="confirmDelete(props.row)">
              <i class="fa fa-times"></i>
            </p-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  import {
    notifySuccess,
    notifyWarning,
    notifyError,
    assemblePlanNameDisplay,
    setIndexColumnWidth,
    getTableHeight
  } from 'src/utilities/utils'
  import Constants from 'src/utilities/constants'
  import Permissions from 'src/utilities/permissions'
  import {OK, NO_CONTENT, UNAUTHORIZED} from 'http-status-codes'

  export default {
    computed: {
      ...mapGetters({
        types: 'types/company',
        statuses: 'statuses/company',
        tableData: 'companies/results'
      }),
      isSearchButtonDisabled() {
        return !this.query.search
      },
      isClearButtonDisabled() {
        return !this.query.search && !this.tableData.length
      },
      canEdit() {
        return this.$can('edit', Permissions.COMPANIES)
      },
      indexColumnWidth() {
        return setIndexColumnWidth(this.tableData.length)
      }
    },
    data() {
      return {
        toDelete: null,
        constants: Constants,
        loading: false,
        query: {
          search: null,
          types: [],
          active: true
        }
      }
    },
    methods: {
      clear() {
        this.$validator.pause()
        this.query = {
          search: "",
          types: [],
          active: true
        }
        this.$store.commit('companies/resetResults')
        setTimeout(this.resumeValidation, 250)
      },
      async fetchAll() {
        this.loading = true
        const response = await this.$store.dispatch('companies/fetchAll', this.query.active)
        this.loading = false
        if (OK !== response.status) {
          notifyError(this, 'Failed to load all companies')
        }
      },
      resumeValidation() {
        this.$validator.resume()
      },
      getError(fieldName) {
        return this.errors.first(fieldName)
      },
      async searchSubmit() {
        if (await this.$validator.validateAll()) {
          this.loading = true
          const response = await this.$store.dispatch('companies/fetchByNameContainsAndTypeAndActive', {
            name: this.query.search.trim(),
            types: this.query.types,
            active: this.query.active
          })
          this.loading = false

          if (OK !== response.status) {
            notifyError(this, 'Failed to execute company search')
            return
          }

          if (response.data.length === 0) {
            this.$eventHub.$emit('show-modal-1', 'No Results Found', null, '500px')
          }
        }
      },
      tableHeight(buffer) {
        return getTableHeight(buffer)
      },
      editCompany(row) {
        this.$store.commit('companies/resetEdit')
        this.$eventHub.$emit('show-modal-2', 'Company', row, '1000px')
      },
      async confirmDelete(company) {
        this.toDelete = company.id
        let showButton = false

        const responseJobs = await this.$store.dispatch('jobs/fetchFindByBuilderIdOrClientId', company.id)

        if (OK !== responseJobs.status) {
          notifyError(this, 'Failed to fetch Jobs. Unable to safely delete company.')
          return
        }

        if (responseJobs.data.length > 0) {
          const message = 'You can not delete <strong>' + company.name + '</strong> as it is referenced by jobs. You may deactive it.'
          this.showConfirmDelete(company, message, showButton)
          return
        }

        const responseCommunity = await this.$store.dispatch('communities/fetchFindByClientIdsOrBuilderIds', company.id)

        if (OK !== responseCommunity.status) {
          notifyError(this, 'Failed to fetch Communities. Unable to safely delete company.')
          return
        }

        let message = 'Are you sure you want to delete <strong>' + company.name + '</strong>? This will completely remove the company.'

        const usedByCommunities = responseCommunity.data
        if (usedByCommunities.length > 0) {
          message = '<strong>' + company.name + '</strong> is used by the following communities and cannot be deleted:'
          message += '<p><ul>'
          usedByCommunities.forEach((usedByCommunities) => {
            message += '<li>' + usedByCommunities.name + '</li>'
          })
          message += '</ul></p>'
          message += '<p>If you really want to delete this company, you must first remove associations to it in each of the communities listed above, then come back here and delete the company.</p>'
          showButton = false
          this.showConfirmDelete(company, message, showButton)
          return
        }

        const responsePriceRules = await this.$store.dispatch('priceRules/fetchFindByBuilderIdOrClientId', company.id)

        if (OK !== responsePriceRules.status) {
          notifyError(this, 'Failed to fetch Price Rules. Unable to safely delete company.')
          return
        }

        const usedByPriceRules = responsePriceRules.data
        if (usedByPriceRules.length > 0) {
          message = '<strong>' + company.name + '</strong> is used by the following price rules and cannot be deleted:'
          message += '<p><ul>'
          usedByPriceRules.forEach((usedByPriceRule) => {
            message += '<li>' + usedByPriceRule.shortId + '</li>'
          })
          message += '</ul></p>'
          message += '<p>If you really want to delete this company, you must first remove associations to it in each of the price rules listed above, then come back here and delete the company.</p>'
          showButton = false
          this.showConfirmDelete(company, message, showButton)
          return
        }

        const responsePlanNames = await this.$store.dispatch('planNames/fetchFindByBuilderId', company.id)

        if (OK !== responsePlanNames.status) {
          notifyError(this, 'Failed to fetch Plan Names. Unable to delete company.')
          return
        }

        const usedByPlanNames = responsePlanNames.data
        if (usedByPlanNames.length > 0) {
          message = '<strong>' + company.name + '</strong> is used by the following plan names and cannot be deleted:'
          message += '<p><ul>'
          usedByPlanNames.forEach((usedByPlanName) => {
            message += '<li>' + assemblePlanNameDisplay(usedByPlanName) + '</li>'
          })
          message += '</ul></p>'
          message += '<p>If you really want to delete this company, you must first remove associations to it in each of the plan names listed above, then come back here and delete the company.</p>'
          showButton = false
          this.showConfirmDelete(company, message, showButton)
          return
        }

        // happy path - company can be deleted
        showButton = true
        this.showConfirmDelete(company, message, showButton)
      },
      showConfirmDelete(company, message, showButton) {
        const payload = {
          message,
          entity: company,
          delete: this.delete,
          showButton
        }
        this.$eventHub.$emit('show-modal-1', 'Confirm Delete', payload, '600px')
      },
      async delete() {
        const response = await this.$store.dispatch('delete', {
          endpoint: 'companies',
          id: this.toDelete
        })

        if (NO_CONTENT === response.status) {
          this.$store.commit('companies/delete', this.toDelete)
          notifySuccess(this, 'Company Deleted Successfully')
        } else if (UNAUTHORIZED === response.status) {
          notifyWarning(this, 'You are not authorized to delete Companies')
        } else {
          notifyError(this, 'Error Deleting Company')
        }
      },
      updateCompanyResult(company) {
        this.$store.set('companies/updateResult', company)
      }
    },
    beforeCreate() {
      this.$store.cache.dispatch('types/fetchByEntityType', 'Company')
    },
    mounted() {
      this.clear()
      this.$eventHub.$on('companySaved', this.updateCompanyResult)
    },
    destroyed() {
      this.$eventHub.$off('companySaved')
    }
  }
</script>

<style scoped>

</style>
